import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardBody, CardFooter, Input, Typography } from "@material-tailwind/react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "redux/store";
import {
  addField,
  fetchFields,
  resetDesignation,
  setDesignation,
  setImage,
} from "redux/features/Admin/Fields/fieldSlice";
import { z } from "zod";
import toast from "react-hot-toast";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { TrashIcon, PhotoIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { EASING } from "../../../utils/easingUtils";

// Zod schema for form validation
const fieldSchema = z.object({
  designation: z
    .string()
    .min(1, "La désignation est requise")
    .max(100, "La désignation ne doit pas dépasser 100 caractères"),
  images: z
    .array(z.instanceof(File))
    .min(1, "Au moins une image est requise")
    .refine(
      (files) => files.every((file) => file.size <= 10 * 1024 * 1024),
      "Chaque image doit être inférieure à 10MB"
    )
    .refine(
      (files) => files.every((file) => ["image/png", "image/jpeg", "image/gif"].includes(file.type)),
      "Seuls les formats PNG, JPG et GIF sont autorisés"
    ),
});

type FieldFormData = z.infer<typeof fieldSchema>;

interface Field {
  designation: string;
  id: number;
  images: string;
}

// Animation variants
const dialogVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: EASING.easeOut } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2, ease: EASING.easeIn } },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASING.easeOut } },
};

interface AddFieldPageProps {
  className?: string;
  onClose: () => void;
}

const AddFieldPage: React.FC<AddFieldPageProps> = ({ className = "", onClose }) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const [loading, setLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);

  const designation = useSelector((state: RootState) => state.featureField.designation);
  const fetchedFields: Field[] = useSelector((state: RootState) => state.featureField.fields);
  const fieldStatus = useSelector((state: RootState) => state.featureField.status);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FieldFormData>({
    resolver: zodResolver(fieldSchema),
    defaultValues: { designation: designation || "", images: [] },
  });

  useEffect(() => {
    dispatch(fetchFields());
  }, [dispatch]);

  useEffect(() => {
    setLoading(fieldStatus === "loading");
  }, [fieldStatus]);

  useEffect(() => {
    reset({ designation, images: uploadedImages });
  }, [designation, uploadedImages, reset]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const updatedImages = [...uploadedImages, ...fileArray];
      setUploadedImages(updatedImages);
      setValue("images", updatedImages, { shouldValidate: true });
      dispatch(setImage(updatedImages));
    }
  };

  const handleDeleteImage = (index: number) => {
    const updatedImages = uploadedImages.filter((_, i) => i !== index);
    setUploadedImages(updatedImages);
    setValue("images", updatedImages, { shouldValidate: true });
    dispatch(setImage(updatedImages));
  };

  const handleClose = () => {
    dispatch(resetDesignation());
    reset();
    onClose();
  };

  const onSubmit = async (data: FieldFormData) => {
    try {
      setLoading(true);
      const isCategoryExists = fetchedFields?.some((field) => field.designation === data.designation);

      if (isCategoryExists) {
        toast.error(`${data.designation} existe déjà !`);
        return;
      }

      const response = await dispatch(addField({ designation: data.designation, images: data.images }));

      if (response.payload?.status === "OK") {
        toast.success("Type de prestation ajouté avec succès !");
        handleClose();
      } else {
        throw new Error(response.payload?.error?.[0]?.msg || "Échec de l'ajout");
      }
    } catch (error: any) {
      toast.error(error.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`nc-AddFieldPage ${className}`} data-nc-id="AddFieldPage">
      <AnimatePresence>
        <motion.div variants={dialogVariants} initial="hidden" animate="visible" exit="exit">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Card className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
              <CardBody className="flex flex-col gap-6 p-6 max-h-[60vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <Typography
                    id="dialog-title"
                    variant="h5"
                    className="w-full p-4 font-bold tracking-tight text-center text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl"
                  >
                    Ajouter un type de prestation
                  </Typography>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="p-2 text-white rounded-full hover:bg-blue-700"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {/* Designation Field */}
                <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                  <label className="font-semibold text-gray-700 dark:text-gray-200">Désignation</label>
                  <div className="relative mt-2">
                    <Input
                      size="lg"
                      type="text"
                      placeholder="Nom du type de prestation"
                      {...register("designation")}
                      onChange={(e) => dispatch(setDesignation(e.target.value))}
                      aria-invalid={!!errors.designation}
                      aria-describedby="designation-error"
                      className="text-gray-900 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                  </div>
                  {errors.designation && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <Typography
                        id="designation-error"
                        variant="small"
                        color="red"
                        className="mt-1 text-xs text-red-500 dark:text-red-400"
                      >
                        {errors.designation.message}
                      </Typography>
                    </motion.div>
                  )}
                </motion.div>

                {/* Image Upload Field */}
                <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                  <label className="font-semibold text-gray-700 dark:text-gray-200">Images</label>
                  <div className="flex justify-center px-6 pt-5 pb-6 mt-2 border-2 border-dashed rounded-lg border-neutral-300 dark:border-neutral-600">
                    <div className="space-y-1 text-center">
                      <PhotoIcon className="w-12 h-12 mx-auto text-neutral-400" />
                      <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                        <label
                          htmlFor="fileInput"
                          className="relative font-medium text-blue-600 rounded-md cursor-pointer hover:text-blue-500"
                        >
                          <span>Ajouter des images</span>
                          <input
                            id="fileInput"
                            type="file"
                            multiple
                            accept="image/png,image/jpeg,image/gif"
                            className="sr-only"
                            onChange={handleFileUpload}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">PNG, JPG, GIF jusqu'à 10MB</p>
                    </div>
                  </div>
                  {uploadedImages.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {uploadedImages.map((image, index) => (
                        <div key={index} className="relative">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`Uploaded ${index}`}
                            className="object-cover w-24 h-24 rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => handleDeleteImage(index)}
                            className="absolute p-1 text-white bg-red-500 rounded-full top-1 right-1 hover:bg-red-600"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.images && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <Typography
                        variant="small"
                        color="red"
                        className="mt-1 text-xs text-red-500 dark:text-red-400"
                      >
                        {errors.images.message}
                      </Typography>
                    </motion.div>
                  )}
                </motion.div>
              </CardBody>
              <CardFooter className="flex justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
                <ButtonSecondary
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-2 text-gray-900 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
                  disabled={loading}
                >
                  Annuler
                </ButtonSecondary>
                <ButtonSecondary
                  type="submit"
                  className="px-6 py-2 text-white rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 hover:from-blue-600 hover:to-indigo-700 dark:hover:from-blue-700 dark:hover:to-indigo-800"
                  disabled={loading}
                  loading={loading}
                >
                  Ajouter
                </ButtonSecondary>
              </CardFooter>
            </Card>
          </form>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AddFieldPage;
